/**
 * Connected applications as staff register them (docs/44_CONNECTED_APPS.md §1).
 *
 * Registering is not approving: an application is `pending` until somebody
 * approves it, and until then the server refuses it everywhere — the consent
 * page, the token endpoint and every token. This file only describes what the
 * server answers; every rule is the server's.
 */

import type { Tone } from '@connect/web-shared';

export const APPS = '/messaging/connected-apps';
export const appKeys = {
  all: ['connected-apps'] as const,
  list: ['connected-apps', 'list'] as const,
  scopes: ['connected-apps', 'scopes'] as const,
  detail: (id: string) => ['connected-apps', 'detail', id] as const,
  connections: (id: string) => ['connected-apps', 'connections', id] as const,
};

export type AppStatus = 'pending' | 'active' | 'paused' | 'suspended';

export interface ConnectedApp {
  public_id: string;
  client_id: string;
  name: string;
  description: string;
  logo_url: string | null;
  homepage_url: string;
  privacy_url: string | null;
  terms_url: string | null;
  redirect_uris: string[];
  scopes: string[];
  webhook_url: string | null;
  verified: boolean;
  listed: boolean;
  status: AppStatus;
  approved_at: string | null;
  paused_at: string | null;
  suspended_at: string | null;
  previous_secret_expires_at: string | null;
  connections: number;
  created_at: string;
  updated_at: string;
}

/** One application in the register (the server's list protocol). */
export interface ConnectedAppRow {
  public_id: string;
  client_id: string;
  name: string;
  description: string;
  logo_url: string | null;
  homepage_url: string;
  status: AppStatus;
  verified: boolean;
  listed: boolean;
  scopes: string[];
  webhook_url: string | null;
  approved_at: string | null;
  created_at: string;
  connections: number;
}

/** A client's connection of an application, as staff see it. */
export interface StaffConnection {
  public_id: string;
  client_public_id: string;
  client_name: string;
  scopes: string[];
  connected_by: string;
  connected_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  revoked_reason: 'client' | 'application' | 'replaced' | 'removed' | null;
}

export const REVOKED_LABELS: Record<string, string> = {
  client: 'Disconnected by the client',
  application: 'Disconnected by the application',
  replaced: 'Connected again',
  removed: 'Application removed',
};

export interface ScopeDef {
  name: string;
  label: string;
}

/** What the server shows once: on registering, or on rotating a secret. */
export interface Secrets {
  client_secret?: string | null;
  webhook_secret?: string | null;
}

export interface AppForm {
  name: string;
  description: string;
  homepage_url: string;
  logo_url: string;
  privacy_url: string;
  terms_url: string;
  /** One address per line, compared exactly by the server. */
  redirect_uris: string;
  scopes: string[];
  webhook_url: string;
  verified: boolean;
  listed: boolean;
}

export const STATUS_TONES: Record<AppStatus, Tone> = {
  pending: 'warning',
  active: 'success',
  paused: 'warning',
  suspended: 'danger',
};
export const STATUS_LABELS: Record<AppStatus, string> = {
  pending: 'Waiting for approval',
  active: 'Approved',
  paused: 'Paused',
  suspended: 'Suspended',
};

export function emptyForm(): AppForm {
  return {
    name: '',
    description: '',
    homepage_url: '',
    logo_url: '',
    privacy_url: '',
    terms_url: '',
    redirect_uris: '',
    scopes: [],
    webhook_url: '',
    verified: false,
    listed: false,
  };
}

export function formOf(app: ConnectedApp): AppForm {
  return {
    name: app.name,
    description: app.description,
    homepage_url: app.homepage_url,
    logo_url: app.logo_url ?? '',
    privacy_url: app.privacy_url ?? '',
    terms_url: app.terms_url ?? '',
    redirect_uris: app.redirect_uris.join('\n'),
    scopes: app.scopes,
    webhook_url: app.webhook_url ?? '',
    verified: app.verified,
    listed: app.listed,
  };
}

/** The request body: blanks are `null`, addresses one per line. */
export function bodyOf(form: AppForm): Record<string, unknown> {
  const blank = (value: string) => (value.trim() ? value.trim() : null);
  return {
    name: form.name.trim(),
    description: form.description.trim(),
    homepage_url: form.homepage_url.trim(),
    logo_url: blank(form.logo_url),
    privacy_url: blank(form.privacy_url),
    terms_url: blank(form.terms_url),
    redirect_uris: form.redirect_uris
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean),
    scopes: form.scopes,
    webhook_url: blank(form.webhook_url),
    verified: form.verified,
    listed: form.listed,
  };
}
